export const CLASS_DAYS = [
    { id: 1, day: 'lunes', simbolo: 'L' },
    { id: 2, day: 'martes', simbolo: 'M' },
    { id: 3, day: 'miércoles', simbolo: 'M' },
    { id: 4, day: 'jueves', simbolo: 'J' },
    { id: 5, day: 'viernes', simbolo: 'V' },
    { id: 6, day: 'sábado', simbolo: 'S' },
];

/** @param {Array<{id:number,simbolo?:string,day?:string}>|undefined} days */
export const sortDays = (days = []) =>
    [...days].sort((a, b) => a.id - b.id);

/** @param {Array<{id:number,simbolo?:string}>|undefined} days @returns {string} */
export const formatDaysLabel = (days = []) => {
    if (!days.length) return '';
    return sortDays(days)
        .map((day) => day.simbolo || CLASS_DAYS.find((item) => item.id === day.id)?.simbolo || '')
        .filter(Boolean)
        .join(', ');
};

/**
 * @param {Array<{id:number,simbolo?:string}>|undefined} days
 * @param {Array<{id:number,time:string}>|undefined} hours
 * @param {string} [placeholder]
 */
export const formatScheduleLabel = (days = [], hours = [], placeholder = 'Seleccionar días y horario') => {
    const daysLabel = formatDaysLabel(days);
    const range = rangeFromSlots(hours);
    const hasHours = hours?.length > 0;
    const timeLabel = hasHours ? formatRangeLabel(range.start, range.end) : '';

    if (daysLabel && timeLabel) return `${daysLabel} · ${timeLabel}`;
    if (daysLabel) return daysLabel;
    if (timeLabel) return timeLabel;
    return placeholder;
};

export const TIME_SLOTS = [
    { id: 1, time: '8:00' },
    { id: 2, time: '8:30' },
    { id: 3, time: '9:00' },
    { id: 4, time: '9:30' },
    { id: 5, time: '10:00' },
    { id: 6, time: '10:30' },
    { id: 7, time: '11:00' },
    { id: 8, time: '11:30' },
    { id: 9, time: '12:00' },
    { id: 10, time: '12:30' },
    { id: 11, time: '13:00' },
    { id: 12, time: '13:30' },
    { id: 13, time: '14:00' },
    { id: 14, time: '14:30' },
    { id: 15, time: '15:00' },
    { id: 16, time: '15:30' },
    { id: 17, time: '16:00' },
    { id: 18, time: '16:30' },
    { id: 19, time: '17:00' },
    { id: 20, time: '17:30' },
    { id: 21, time: '18:00' },
    { id: 22, time: '18:30' },
    { id: 23, time: '19:00' },
    { id: 24, time: '19:30' },
    { id: 25, time: '20:00' },
    { id: 26, time: '20:30' },
    { id: 27, time: '21:00' },
    { id: 28, time: '21:30' },
    { id: 29, time: '22:00' },
    { id: 30, time: '22:30' },
    { id: 31, time: '23:00' },
    { id: 32, time: '23:30' },
];

export const HOURS_12 = Array.from({ length: 12 }, (_, index) => index + 1);
export const MINUTES_30 = [0, 30];
export const PERIODS = ['AM', 'PM'];

/** Slots cada 30 min de 00:00 a 23:30, más 23:59 para cierre de día. */
export const DAY_TIME_SLOTS = (() => {
    const slots = [];
    for (let hour = 0; hour < 24; hour += 1) {
        for (const minute of [0, 30]) {
            slots.push(`${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`);
        }
    }
    slots.push('23:59');
    return slots;
})();

/** @param {string} time24 @returns {string} */
export const snapToDayTimeSlot = (time24) => {
    if (!time24 || DAY_TIME_SLOTS.includes(time24)) return time24 || '08:00';

    const [hours, minutes] = time24.split(':').map(Number);
    const totalMinutes = hours * 60 + minutes;
    let closest = DAY_TIME_SLOTS[0];
    let minDiff = Infinity;

    DAY_TIME_SLOTS.forEach((slot) => {
        const [slotHours, slotMinutes] = slot.split(':').map(Number);
        const diff = Math.abs(slotHours * 60 + slotMinutes - totalMinutes);
        if (diff < minDiff) {
            minDiff = diff;
            closest = slot;
        }
    });

    return closest;
};

/**
 * @typedef {{ hour: number, minute: number, period: 'AM' | 'PM' }} TimeParts
 */

/** @returns {TimeParts} */
export const getNowTimeParts = () => {
    const now = new Date();
    let hour = now.getHours();
    const minute = now.getMinutes() >= 30 ? 30 : 0;
    const period = hour >= 12 ? 'PM' : 'AM';
    let hour12 = hour % 12;
    if (hour12 === 0) hour12 = 12;
    return { hour: hour12, minute, period };
};

/** @param {number} hour @param {number} minute @param {'AM'|'PM'} period @returns {string} */
export const to24HourString = (hour, minute, period) => {
    let hour24 = hour % 12;
    if (period === 'PM') hour24 += 12;
    if (period === 'AM' && hour === 12) hour24 = 0;
    return `${String(hour24).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
};

/** @param {string} time24 @returns {TimeParts} */
export const from24HourString = (time24) => {
    const [hour24, minute] = time24.split(':').map(Number);
    const period = hour24 >= 12 ? 'PM' : 'AM';
    let hour = hour24 % 12;
    if (hour === 0) hour = 12;
    return { hour, minute, period };
};

/** @param {string} time24 @returns {{ id: number, time: string }} */
export const snapToNearestSlot = (time24) => {
    const [h, m] = time24.split(':').map(Number);
    const totalMinutes = h * 60 + m;
    let closest = TIME_SLOTS[0];
    let minDiff = Infinity;

    TIME_SLOTS.forEach((slot) => {
        const [sh, sm] = slot.time.split(':').map(Number);
        const diff = Math.abs(sh * 60 + sm - totalMinutes);
        if (diff < minDiff) {
            minDiff = diff;
            closest = slot;
        }
    });

    return closest;
};

/** @param {string} startTime @param {string} endTime @returns {Array<{id:number,time:string}>} */
export const buildSlotRange = (startTime, endTime) => {
    const start = TIME_SLOTS.find((slot) => slot.time === startTime);
    const end = TIME_SLOTS.find((slot) => slot.time === endTime);
    if (!start || !end) return [];

    const lo = Math.min(start.id, end.id);
    const hi = Math.max(start.id, end.id);
    return TIME_SLOTS.filter((slot) => slot.id >= lo && slot.id <= hi);
};

/** @param {Array<{id:number,time:string}>|undefined} slots @returns {{ start: string, end: string }} */
export const rangeFromSlots = (slots) => {
    if (!slots?.length) {
        return { start: '08:00', end: '09:00' };
    }

    return {
        start: slots[0].time,
        end: slots[slots.length - 1].time,
    };
};

/** @param {TimeParts} parts @returns {string} */
export const formatDisplayTime = ({ hour, minute, period }) =>
    `${String(hour).padStart(2, '0')} : ${String(minute).padStart(2, '0')} ${period}`;

/** @param {string} time24 @returns {string} */
export const format24Display = (time24) => {
    const { hour, minute, period } = from24HourString(time24);
    return formatDisplayTime({ hour, minute, period });
};

/** @param {string} start @param {string} end @returns {string} */
export const formatRangeLabel = (start, end) =>
    `${format24Display(start)} — ${format24Display(end)}`;
